<template>
  <div>
    <div class="editor-keypad-top">
      <div class="editor-keypad-title">{{title}}</div>
      <div class="editor-keypad-value">{{value | formatInteger}}{{suffix}}</div>
    </div>
    <div class="editor-keypad-error-area">
      <template v-if="errorText">
        <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" fixedWidth />
        <span class="text-danger ml-1">{{errorText}}</span>
      </template>
    </div>

    <div class="row no-gutters editor-keypad-row">
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(7)">7</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(8)">8</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(9)">9</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button text-danger" @click.prevent="onResetPressed">C</button>        
      </div>
    </div>
    <div class="row no-gutters editor-keypad-row">
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(4)">4</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(5)">5</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(6)">6</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onLockPressed()" v-if="showLock && !locked"><i class="far fa-lock"></i></button>
        <button class="btn btn-default btn-block editor-keypad-button active editor-keypad-lock" @click.prevent="onUnlockPressed()" v-if="showLock && locked"><i class="far fa-unlock"></i></button>
      </div>
    </div>
    <div class="row no-gutters editor-keypad-row">
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(1)">1</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(2)">2</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(3)">3</button>
      </div>
      <div class="col-3">
        <button :class="['btn', 'btn-default', 'btn-block', 'editor-keypad-button', 'editor-keypad-direction', { 'active': extendDirectionIndex==0}]" @click.prevent="onLeftExtendPressed()" v-if="showExtendDirection" :disabled="extendDirectionDisabled">
          <i class="far fa-arrow-from-right" :style="extendLeftIconStyle"></i>
        </button>
      </div>
    </div>
    <div class="row no-gutters editor-keypad-row">
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onPlusMinusPressed()" :disabled="!showPlusMinus">&plusmn;</button>
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDecimalPressed(7)" v-if="false">.</button>   <!-- Decimal disabled until we actually need it -->
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onDigitPressed(0)">0</button>
      </div>
      <div class="col-3">
        <button class="btn btn-default btn-block editor-keypad-button" @click.prevent="onBackspacePressed(7)"><i class="fas fa-backspace"></i></button>
      </div>
      <div class="col-3">
        <button :class="['btn', 'btn-default', 'btn-block', 'editor-keypad-button', 'editor-keypad-direction', { 'active': extendDirectionIndex==1}]" @click.prevent="onRightExtendPressed()" v-if="showExtendDirection" :disabled="extendDirectionDisabled">
          <i class="far fa-arrow-from-right" :style="extendRightIconStyle"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.editor-keypad-top {
  margin-bottom: 6px;
}
.editor-keypad-title {
  font-size: 16px;
  text-transform: uppercase !important;
}
.editor-keypad-value {
  font-size: 18px;
  font-weight: bold;
}
.editor-keypad-button {
  font-size: 16px;
  font-weight: bold;
  height: 44px;
  line-height: 44px;
  padding: inherit;
}
.editor-keypad-button.editor-keypad-lock.active {
  background-color: #ad473a !important;
  color: #ffffff !important;
}
.editor-keypad-button.editor-keypad-direction.active {
  background-color: #45aaf2 !important;
  color: #ffffff !important;
}
.editor-keypad-error-area {
  height: 1rem;
  min-height: 1rem;
  font-size: 0.65rem;
}
</style>

<script>
export default {
  name: 'EditorKeypad',
  model: {
    event: 'update'
  },
  props: {
    title: String,
    showPlusMinus: {
      type: Boolean,
      default: true
    },
    showLock: {
      type: Boolean,
      default: false
    },
    showExtendDirection: {
      type: Boolean,
      default: false
    },    
    extendDirectionDisabled: {
      type: Boolean,
      default: false
    },
    extendDirectionIndex: {
      type: Number,
      default: 0
    },
    leftDirectionAngle: {
      type: Number,
      default: 0
    },
    rightDirectionAngle: {
      type: Number,
      default: 0
    },
    suffix: {
      type: String,
      default: ''
    },
    errorText: String,
    value: [String, Number],
    locked: Boolean
  },
  computed: {
    extendLeftIconStyle() {
      let angleDeg = this.leftDirectionAngle
      return {
        'transform': `rotate(${angleDeg}deg)`
      }
    },
    extendRightIconStyle() {
      let angleDeg = this.rightDirectionAngle
      return {
        'transform': `rotate(${angleDeg}deg)`
      }
    },
  },
  methods: {
    onDigitPressed(digit) {
      var number = this.value.toString()
      number += digit.toString()
      this.updateNumber(number)
    },
    onResetPressed() {
      this.updateNumber(0)      
    },
    onDecimalPressed() {
      // TODO: This needed for glass? Maybe angles
    },
    onBackspacePressed() {
      var number = this.value.toString()
      if(number.length > 0) {
        number = number.substring(0, number.length-1)
        this.updateNumber(number)
      }
    },
    onPlusMinusPressed() {
      var val = parseFloat(this.value.toString())
      if(isNaN(val))
        val = 0
      if(val != 0.0)
        this.updateNumber(-val)
    },
    onUnlockPressed() {
      this.$emit('updateLock', false)
    },
    onLockPressed() {
      this.$emit('updateLock', true)
    },
    updateNumber(number) {
      var val = parseFloat(number)
      if(isNaN(val))
        val = 0
      this.$emit('update', val)
    },
    onLeftExtendPressed() {
      this.$emit('updateDirection', 0)
    },
    onRightExtendPressed() {
      this.$emit('updateDirection', 1)
    }
  }

}
</script>